import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeleteOutline, SearchOutlined } from '@material-ui/icons';
import { Button } from '@mui/material';
import { Stack } from '@mui/system';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import * as React from "react";
import { IDomainInDTO } from '../../repository/jago/model/input/IDomainInDTO';
import { IWalletInDTO } from '../../repository/jago/model/input/IWalletInDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';
import Utils from '../../utils/Utils';
import ReduxLanguage from '../ReduxLanguage';

const styles = createStyles({
  rowContainer: {
    display: 'flex',
    margin: 10,
    flexDirection: 'column'
  },
  rowBodyContainer: {
    padding: 15,
    minHeight: 100,
  },
  rowTitle: {
    fontWeight: 'bold'
  },
  logo: {
    width: '20%',
  },
});


export interface ICustomerDomainsTableState {
}

export interface ICustomerDomainsTableProps extends WithStyles<typeof styles>{
  customerDomains: PaginatedDataDTO<IDomainInDTO>;
  tableColumns?: GridColumns<IDomainInDTO>;
  onRowClick?: (customerDomain: IDomainInDTO) => void;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDeleteDomainRequest?: (domain: IDomainInDTO) => void;
  wallets: PaginatedDataDTO<IWalletInDTO>;
  rowsPerPage: number;
  rowHeight?: number;
  disableSelectionOnClick?: boolean;
  isLoading?: boolean;
  onShowWalletDetails: (wallet: IWalletInDTO) => void;
}

class CustomerDomainsTable extends React.Component<ICustomerDomainsTableProps, ICustomerDomainsTableState> {
  private defaultColumns: GridColumns<IDomainInDTO>;
  public render() {
    const { customerDomains, onShowWalletDetails, rowsPerPage, wallets, disableSelectionOnClick, onRowClick, onPageSizeChange, onFetchElementsOnPage, tableColumns, rowHeight, onDeleteDomainRequest } = this.props;
    this.defaultColumns = [
      {
        field: 'name',
        headerName: translations.t('gateways.name'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerDomain = params.row;
          return (
            <div>
              <h3 style={{ margin: 0,  fontWeight: 'bold' }}>
                {customerDomain.name}
              </h3>
              <h4 style={{ margin: 0,  fontWeight: 'normal' }}>
                {customerDomain.hostname}
              </h4>
            </div>
        )}
      },
      {
        field: 'type',
        headerName: translations.t('credits.customerPlantType'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 0.8,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerDomain = params.row;
          return (
            <div>
              <span style={{ fontWeight: 'normal' }}>{customerDomain.type ? Utils.getTranslatedTypeLabel(customerDomain.type) : '-'}</span>
            </div>
          );
        }
      },
      {
        field: 'solution',
        headerName: translations.t('credits.customerPlantSolution'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 0.8,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerDomain = params.row;
          return (
            <div>
              <span style={{ fontWeight: 'normal' }}>{customerDomain.solution ? Utils.getTranslatedSolutionLabel(customerDomain.solution) : '-'}</span>
            </div>
          );
        }
      },
      {
        field: 'platform',
        headerName: translations.t('credits.customerPlantPlatform'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 0.8,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerDomain = params.row;
          return (
            <div>
              <span style={{ fontWeight: 'normal' }}>{customerDomain.platform ? Utils.getTranslatedPlatformLabel(customerDomain.platform) : '-'}</span>
            </div>
          );
        }
      },
      {
        field: 'management',
        headerName: translations.t('credits.customerPlantManagement'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 0.8,
        renderCell: (params: GridRenderCellParams<any>) => {
          const customerDomain = params.row;
          return (
            <div>
              <span style={{ fontWeight: 'normal' }}>{customerDomain.management ? Utils.getTranslatedManagementLabel(customerDomain.management) : '-'}</span>
            </div>
          );
        }
      },
      ...(AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_PLANTS_DELETE) ? [{
        field: 'actions',
        headerName: translations.t('forms.actions'),
        renderCell: (params: any) => {
          const customerDomain = params.row;
          const canDelete = customerDomain && !(customerDomain.solution === 'LUCKEY' && customerDomain.management === 'SAAS')
          return (
            canDelete ? (
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'darkred',
                  flexShrink: 0,
                  marginRight: 20,
                  width: 'fit-content',
                  color: 'white',
                  padding: 4,
                  paddingLeft: 10,
                  paddingRight: 10,
                  fontWeight: 'bold'
                }}
                onClick={(e) => {e.stopPropagation(); onDeleteDomainRequest(params.row)}}
              >
                <DeleteOutline style={{ fontSize: 15 }}/>
                <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"forms.delete"} /></h5>
              </Button>
            ) : null
          )
        },
        type: 'string',
        sortable: false,
        minWidth: 150,
      }] : []),
    ];

    return (
      <DataGrid
        getRowId={(d) => d.id}
        rows={customerDomains.content || []}
        columns={tableColumns ? tableColumns: this.defaultColumns}
        rowHeight={rowHeight ? rowHeight: 55}
        onPageSizeChange={(pageSize) => onPageSizeChange(pageSize)}
        onPageChange={page => onFetchElementsOnPage(page)}
        page={(customerDomains?.pagination as PaginationDTO)?.number}
        rowCount={(customerDomains?.pagination as PaginationDTO)?.totalElements}
        pageSize={rowsPerPage}
        loading={this.props.isLoading}
        onRowClick={row => {
          const customerDomain = row.row;
          const wallet = customerDomain.walletId ? (wallets.content || []).find(wallet => wallet.id === customerDomain.walletId) : undefined
          if (wallet) {
            if (onShowWalletDetails)
              onShowWalletDetails(wallet)
          }
          else {
            if (onRowClick)
              onRowClick(customerDomain)
          }
        }}
        paginationMode="server"
        disableSelectionOnClick={disableSelectionOnClick}
        componentsProps={{
          pagination: {
            labelRowsPerPage: translations.t('forms.rowsPerPage')
          }
        }}
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <SearchOutlined style={{ alignSelf: 'center' }} />
                <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noCustomerDomainFindTitle' /></h3>
                <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noCustomerDomainFindDescription' /></h4>
              </div>
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='credits.noCustomerDomainFindTitle' /></h3>
              <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='credits.noCustomerDomainFindDescription' /></h4>
            </Stack>
          )
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    );
  }
}

export default withStyles(styles)(CustomerDomainsTable);